/**
 * Losi Site Experiences
 *
 * Shared metadata for Chat, Library, and Sites workbench surfaces.
 *
 * Primary responsibilities:
 * - Define the Sites creation types Losi supports
 * - Keep Chat prompts and UI labels consistent
 *
 * Dependencies:
 * - None
 */

export type LosiSiteExperienceId =
  | 'website'
  | 'landing-page'
  | 'form'
  | 'quiz'
  | 'calculator'
  | 'portal'
  | 'mini-app'
  | 'interactive-page'

export interface LosiSiteExperience {
  automationHooks: readonly string[]
  description: string
  deliverables: readonly string[]
  defaultSections: readonly string[]
  id: LosiSiteExperienceId
  inputs: readonly string[]
  label: string
  knowledgeNeeds: readonly string[]
  prompt: string
}

export const LOSI_SITE_EXPERIENCES = [
  {
    automationHooks: ['Lead capture', 'Newsletter signup', 'Contact routing'],
    description: 'A multi-page public website with content, structure, and calls to action.',
    deliverables: ['Sitemap', 'Homepage outline', 'Core page briefs', 'SEO checklist'],
    defaultSections: ['Home', 'About', 'Services', 'Proof', 'Contact'],
    id: 'website',
    inputs: ['Audience', 'Goal', 'Pages', 'Brand direction', 'Launch target'],
    label: 'Website',
    knowledgeNeeds: ['Brand voice', 'Offer details', 'Customer proof', 'Contact details'],
    prompt: 'Create a Losi Sites website brief. Ask me for the audience, goal, pages, content, style, and launch target.'
  },
  {
    automationHooks: ['Lead form', 'CRM handoff', 'Campaign tracking'],
    description: 'A focused conversion page for one offer, audience, or campaign.',
    deliverables: ['Offer hierarchy', 'Section outline', 'CTA plan', 'A/B test ideas'],
    defaultSections: ['Hero', 'Problem', 'Offer', 'Proof', 'FAQ', 'CTA'],
    id: 'landing-page',
    inputs: ['Offer', 'Audience', 'Conversion goal', 'Proof', 'Traffic source'],
    label: 'Landing Page',
    knowledgeNeeds: ['Offer details', 'Testimonials', 'Pricing', 'Campaign context'],
    prompt: 'Create a Losi Sites landing page brief. Ask me for the offer, audience, proof, sections, and conversion goal.'
  },
  {
    automationHooks: ['Submission storage', 'Email notification', 'Workspace task creation'],
    description: 'Forms are part of Sites, including intake, lead capture, and applications.',
    deliverables: ['Field list', 'Validation rules', 'Submission states', 'Follow-up flow'],
    defaultSections: ['Intro', 'Fields', 'Consent', 'Confirmation'],
    id: 'form',
    inputs: ['Fields', 'Validation', 'Audience', 'Submission destination', 'Follow-up'],
    label: 'Form',
    knowledgeNeeds: ['Privacy wording', 'Field definitions', 'Routing rules'],
    prompt: 'Create a Losi Sites form. Ask me what the form collects, who fills it out, field rules, and what should happen after submission.'
  },
  {
    automationHooks: ['Result capture', 'Segment assignment', 'Follow-up email'],
    description: 'Interactive quizzes for qualification, education, recommendations, or scoring.',
    deliverables: ['Question set', 'Scoring model', 'Result pages', 'Follow-up flow'],
    defaultSections: ['Intro', 'Questions', 'Scoring', 'Results', 'CTA'],
    id: 'quiz',
    inputs: ['Quiz goal', 'Questions', 'Scoring', 'Outcomes', 'Follow-up action'],
    label: 'Quiz',
    knowledgeNeeds: ['Audience segments', 'Recommendation logic', 'Offer mapping'],
    prompt: 'Create a Losi Sites quiz. Ask me for the audience, quiz goal, questions, scoring, outcomes, and follow-up action.'
  },
  {
    automationHooks: ['Result capture', 'Quote request', 'CRM update'],
    description: 'Calculators for estimates, pricing, eligibility, ROI, planning, or comparison.',
    deliverables: ['Input model', 'Formula notes', 'Result layout', 'Assumption copy'],
    defaultSections: ['Inputs', 'Assumptions', 'Calculation', 'Results', 'Next step'],
    id: 'calculator',
    inputs: ['Inputs', 'Formula', 'Assumptions', 'Result format', 'Next action'],
    label: 'Calculator',
    knowledgeNeeds: ['Pricing rules', 'Eligibility rules', 'Disclaimers'],
    prompt: 'Create a Losi Sites calculator. Ask me for inputs, formulas, outputs, assumptions, and the user action after results.'
  },
  {
    automationHooks: ['Login gate', 'Request routing', 'Status notifications'],
    description: 'A gated or role-based experience for clients, customers, members, or teams.',
    deliverables: ['Role map', 'Screen list', 'Data needs', 'Permission review'],
    defaultSections: ['Login', 'Dashboard', 'Requests', 'Files', 'Profile'],
    id: 'portal',
    inputs: ['Roles', 'Screens', 'Data sources', 'Permissions', 'First workflows'],
    label: 'Portal',
    knowledgeNeeds: ['User roles', 'Policy rules', 'Data ownership', 'Support process'],
    prompt: 'Create a Losi Sites portal brief. Ask me who logs in, what they see, roles, data needs, and first workflows.'
  },
  {
    automationHooks: ['Task creation', 'Data update', 'Approval request'],
    description: 'A small focused web app for a task, workflow, or customer interaction.',
    deliverables: ['Screen flow', 'State model', 'Action list', 'Error states'],
    defaultSections: ['Start', 'Input', 'Review', 'Action', 'Confirmation'],
    id: 'mini-app',
    inputs: ['Task', 'Screens', 'Data', 'Actions', 'Success criteria'],
    label: 'Mini App',
    knowledgeNeeds: ['Workflow rules', 'Data definitions', 'Approval policy'],
    prompt: 'Create a Losi Sites mini app brief. Ask me what task it solves, screens, data, actions, states, and success criteria.'
  },
  {
    automationHooks: ['Embed event', 'Lead capture', 'Result export'],
    description: 'A custom interactive page with guided controls, previews, embeds, or tools.',
    deliverables: ['Interaction model', 'Controls', 'Preview states', 'Embed plan'],
    defaultSections: ['Intro', 'Controls', 'Preview', 'Output', 'CTA'],
    id: 'interactive-page',
    inputs: ['Interaction goal', 'Controls', 'Content', 'Outputs', 'Visual style'],
    label: 'Interactive Page',
    knowledgeNeeds: ['Content sources', 'Visual references', 'Output rules'],
    prompt: 'Create a Losi Sites interactive page. Ask me what the user should explore, controls, content, outputs, and visual style.'
  }
] as const satisfies readonly LosiSiteExperience[]

export const DEFAULT_LOSI_SITE_EXPERIENCE = LOSI_SITE_EXPERIENCES[0]

export function isLosiSiteExperienceId(value: unknown): value is LosiSiteExperienceId {
  return (
    typeof value === 'string' &&
    LOSI_SITE_EXPERIENCES.some((experience) => experience.id === value)
  )
}

export function getLosiSiteExperience(
  id: LosiSiteExperienceId | null
): LosiSiteExperience {
  return LOSI_SITE_EXPERIENCES.find((experience) => experience.id === id) ??
    DEFAULT_LOSI_SITE_EXPERIENCE
}
